import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  PlaneGeometry,
  Vector3,
} from 'three';
import { addStaticCollider } from './util.js';
import { clamp, damp, dampAngle } from '../../../utils/math.js';
import { createPoliceAssets } from './policeAssets.js';

const LANE_CENTER = 6.45;
const CHECKPOINT_INSET = 40;

// DShK engagement envelope. Each lane's gunner faces the traffic approaching
// that carriageway and turns his pedestal gun onto the Hilux inside a frontal
// arc. A hit is deliberately heavy: 20 health is one fifth of a full bar.
const DSHK_RANGE = 32;
const DSHK_DAMAGE = 20;
const DSHK_AIM_DELAY = 0.75;
const DSHK_SHOT_INTERVAL = 1.25;
const DSHK_MAX_YAW = 0.7;
const DSHK_MIN_PITCH = -0.22;
const DSHK_MAX_PITCH = 0.55;
const DSHK_FLASH_TIME = 0.055;
const DSHK_TRACER_TIME = 0.09;
const DSHK_TARGET_HEIGHT = 1.0;
const DSHK_PIVOT_Z = -1.3;
const DSHK_PIVOT_Y = 2.46;

// Reused each frame to keep checkpoint gunnery allocation-free.
const TARGET = new Vector3();
const LOCAL_TARGET = new Vector3();
const MUZZLE = new Vector3();

/**
 * Sudanese police checkpoints at both ends of Bashir Elnifidi Street.
 *
 * Each checkpoint has a pair of white/blue Toyota-style police pickups, one
 * per carriageway. Both carry a pedestal-mounted DShK and a gunner who covers
 * that road's approach, while red-and-white barricades, traffic cones,
 * bilingual police signs and officers in the regular police light-blue khaki
 * uniform complete the checkpoint.
 */
export function buildPoliceCheckpoints(level, route) {
  const { materials, templates } = createPoliceAssets();
  const specs = [
    { id: 'south', s: CHECKPOINT_INSET, label: 'Wad Madani approach' },
    {
      id: 'north',
      s: route.length - CHECKPOINT_INSET,
      label: 'Al-Mashtal approach',
    },
  ];

  const tracerGeometry = new CylinderGeometry(0.026, 0.002, 1, 6);
  tracerGeometry.rotateX(Math.PI / 2);
  const checkpoints = specs.map((spec) =>
    buildCheckpoint(level, route, materials, templates, spec, tracerGeometry));

  let elapsed = 0;
  const police = {
    checkpoints,
    materials,
    /** Called once when a gunner first acquires the Hilux. */
    onWarning: null,
    /** Called whenever a DShK round leaves the barrel. */
    onShot: null,
    update(dt, player) {
      elapsed += dt;
      const phase = Math.floor(elapsed * 7) % 2;
      const blueOn = phase === 0;
      materials.blueLens.color.setHex(blueOn ? 0x4d7cff : 0x14244f);
      materials.redLens.color.setHex(blueOn ? 0x4d1118 : 0xff3b4d);
      const beaconOn = Math.sin(elapsed * 5.5) > 0.15;
      materials.beacon.color.setHex(beaconOn ? 0xffab2f : 0x76501b);

      for (const checkpoint of checkpoints) {
        updateCheckpoint(checkpoint, dt, player, police);
      }
    },
  };
  return police;
}

function buildCheckpoint(level, route, materials, templates, spec, tracerGeometry) {
  const frame = route.frameAt(spec.s);
  const group = new Group();
  group.position.copy(frame.position);
  group.rotation.y = frame.yaw;
  group.name = `police-checkpoint-${spec.id}`;
  level.root.add(group);

  const toWorld = (x, z, y = 0) => {
    const point = frame.position
      .clone()
      .addScaledVector(frame.right, x)
      .addScaledVector(frame.tangent, -z);
    point.y = y;
    return point;
  };

  const addCollider = (x, z, width, height, depth, yaw, y = height / 2) => {
    const point = toWorld(x, z, y);
    addStaticCollider(level, {
      x: point.x,
      y,
      z: point.z,
      width,
      height,
      depth,
      yaw: frame.yaw + yaw,
    });
  };

  let truckCount = 0;
  let armedTruckCount = 0;
  const gunners = [];

  for (const lane of [-1, 1]) {
    const laneX = lane * LANE_CENTER;
    const approach = lane;

    // Red-and-white barricades block both carriageways.
    for (const offset of [-2.4, 0, 2.4]) {
      const barricade = templates.barricade.clone(true);
      barricade.position.set(laneX + offset, 0.02, 0);
      group.add(barricade);
      addCollider(laneX + offset, 0, 2.36, 1.18, 0.62, 0);
    }

    addBeacon(group, materials, laneX + lane * 2.4, 1.16, 0);

    // Stop paddle mounted above the barricades, facing approaching traffic.
    const post = new Mesh(new BoxGeometry(0.055, 0.5, 0.055), materials.dark);
    post.position.set(laneX, 1.26, approach * 0.15);
    group.add(post);
    const stopSign = new Mesh(new PlaneGeometry(0.82, 0.82), materials.stopSign);
    stopSign.position.set(laneX, 1.6, approach * 0.16);
    stopSign.rotation.y = approach > 0 ? 0 : Math.PI;
    group.add(stopSign);

    // A police pickup waits behind each barrier, angled toward the centre of
    // the road and facing the traffic that approaches that carriageway. Every
    // pickup carries a DShK and gunner, so neither approach is unguarded.
    const truckZ = -approach * 4.15;
    const truckYaw = approach > 0 ? -0.22 : Math.PI - 0.22;
    const truck = templates.armedTruck.clone(true);
    truck.position.set(laneX, 0.02, truckZ);
    truck.rotation.y = truckYaw;
    group.add(truck);
    addCollider(laneX, truckZ, 2.04, 2.15, 5.55, truckYaw, 1.075);
    truckCount += 1;

    const aim = truck.getObjectByName('dshk-aim');
    const elevation = truck.getObjectByName('dshk-elevation');
    const flash = truck.getObjectByName('dshk-muzzle-flash');
    if (aim && elevation && flash) {
      const tracer = new Mesh(tracerGeometry, materials.tracer);
      tracer.visible = false;
      tracer.frustumCulled = false;
      level.root.add(tracer);
      gunners.push({
        lane,
        truck,
        aim,
        elevation,
        flash,
        tracer,
        aimTime: 0,
        cooldown: 0,
        flashTime: 0,
        tracerTime: 0,
        yaw: 0,
        pitch: 0,
        engaged: false,
      });
      armedTruckCount += 1;
    }

    // Traffic officer with a raised white-gloved hand.
    const directorX = lane * 8.7;
    const directorZ = approach * 2.0;
    const director = templates.director.clone(true);
    director.position.set(directorX, 0.02, directorZ);
    director.rotation.y = approach > 0 ? 0 : Math.PI;
    group.add(director);

    // Cones narrow the approach and make the checkpoint read from a distance.
    for (let i = 0; i < 2; i += 1) {
      const cone = templates.cone.clone(true);
      cone.position.set(
        laneX + (i === 0 ? -2.2 : 2.2),
        0.02,
        approach * (6.5 + i * 5),
      );
      cone.rotation.y = i * 0.5;
      group.add(cone);
    }

    // A bilingual Sudan Police checkpoint sign sits on the shoulder.
    const sign = templates.sign.clone(true);
    sign.position.set(lane * 12.05, 0.02, approach * 8.4);
    sign.rotation.y = approach > 0 ? 0 : Math.PI;
    group.add(sign);
  }

  // A supervising officer stands near the median, facing the barrier.
  const supervisor = templates.standing.clone(true);
  supervisor.position.set(3.15, 0.02, 4.0);
  supervisor.rotation.y = -0.35;
  group.add(supervisor);

  return {
    ...spec,
    frame,
    group,
    gunners,
    truckCount,
    armedTruckCount,
    officerCount: 3 + gunners.length,
    logo: 'SUDAN POLICE',
  };
}

function updateCheckpoint(checkpoint, dt, player, police) {
  for (const gunner of checkpoint.gunners) {
    updateGunner(gunner, checkpoint, dt, player, police);
  }
}

/**
 * Track the player's truck inside the gunner's frontal arc, then fire at the
 * fixed cadence. The gun flashes and sends a visible tracer before damage is
 * applied, so a hit is always attributable to the DShK rather than to a car.
 */
function updateGunner(gunner, checkpoint, dt, player, police) {
  gunner.flashTime = Math.max(0, gunner.flashTime - dt);
  gunner.flash.visible = gunner.flashTime > 0;
  gunner.tracerTime = Math.max(0, gunner.tracerTime - dt);
  gunner.tracer.visible = gunner.tracerTime > 0;
  gunner.cooldown = Math.max(0, gunner.cooldown - dt);

  let valid = false;
  const playerPosition = player?.mesh?.position;
  if (playerPosition && (player?.health ?? 0) > 0) {
    TARGET.copy(playerPosition);
    TARGET.y += DSHK_TARGET_HEIGHT;
    LOCAL_TARGET.copy(TARGET);
    gunner.truck.worldToLocal(LOCAL_TARGET);

    const dx = LOCAL_TARGET.x;
    const dz = LOCAL_TARGET.z - DSHK_PIVOT_Z;
    const horizontal = Math.hypot(dx, dz);
    const yaw = Math.atan2(dx, dz);
    const pitch = Math.atan2(
      DSHK_PIVOT_Y - LOCAL_TARGET.y,
      Math.max(horizontal, 0.001),
    );

    valid = horizontal >= 2
      && horizontal <= DSHK_RANGE
      && Math.abs(yaw) <= DSHK_MAX_YAW;

    if (valid) {
      gunner.yaw = dampAngle(
        gunner.yaw,
        clamp(yaw, -DSHK_MAX_YAW, DSHK_MAX_YAW),
        7.5,
        dt,
      );
      gunner.pitch = damp(
        gunner.pitch,
        clamp(pitch, DSHK_MIN_PITCH, DSHK_MAX_PITCH),
        9,
        dt,
      );
    }
  }

  if (!valid) {
    gunner.yaw = damp(gunner.yaw, 0, 5, dt);
    gunner.pitch = damp(gunner.pitch, 0, 5, dt);
    gunner.engaged = false;
    gunner.aimTime = 0;
  }

  gunner.aim.rotation.y = gunner.yaw;
  gunner.elevation.rotation.x = gunner.pitch;

  if (!valid) return;

  if (!gunner.engaged) {
    gunner.engaged = true;
    gunner.aimTime = 0;
    police.onWarning?.({
      checkpointId: checkpoint.id,
      checkpointName: checkpoint.label,
      lane: gunner.lane,
    });
  }

  gunner.aimTime += dt;
  if (gunner.aimTime < DSHK_AIM_DELAY || gunner.cooldown > 0) return;

  fireGunner(gunner, checkpoint, player, police);
  gunner.cooldown = DSHK_SHOT_INTERVAL;
}

function fireGunner(gunner, checkpoint, player, police) {
  TARGET.copy(player.mesh.position);
  TARGET.y += DSHK_TARGET_HEIGHT;

  // Resolve the muzzle after this frame's traverse so the tracer starts at
  // the moving barrel rather than at last frame's position.
  gunner.aim.updateWorldMatrix(true, true);
  gunner.flash.getWorldPosition(MUZZLE);

  const distance = MUZZLE.distanceTo(TARGET);
  gunner.flashTime = DSHK_FLASH_TIME;
  gunner.flash.visible = true;
  gunner.tracerTime = DSHK_TRACER_TIME;
  gunner.tracer.visible = true;
  gunner.tracer.position.copy(MUZZLE).lerp(TARGET, 0.5);
  gunner.tracer.lookAt(TARGET);
  gunner.tracer.scale.set(1, 1, Math.max(distance, 0.1));

  police.onShot?.({
    checkpointId: checkpoint.id,
    checkpointName: checkpoint.label,
    lane: gunner.lane,
    distance,
  });

  player.damage?.(DSHK_DAMAGE, {
    source: 'dshk',
    checkpointId: checkpoint.id,
    checkpointName: checkpoint.label,
    distance,
  });
}

function addBeacon(group, materials, x, y, z) {
  const base = new Mesh(new CylinderGeometry(0.11, 0.14, 0.08, 10), materials.dark);
  base.position.set(x, y, z);
  const lamp = new Mesh(new CylinderGeometry(0.095, 0.105, 0.16, 10), materials.beacon);
  lamp.position.set(x, y + 0.1, z);
  base.castShadow = true;
  lamp.castShadow = true;
  group.add(base, lamp);
}
